function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */


import React, { Component, memo, createRef } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import tabbable from 'tabbable';
import { EuiPopover } from '../popover';
import { EuiScreenReaderOnly } from '../accessibility';
import { EuiI18n } from '../i18n';
import { EuiButtonIcon } from '../button';
import { EuiMutationObserver } from '../observer/mutation_observer';
import { DataGridContext } from './data_grid_context';
import { EuiFocusTrap } from '../focus_trap';
var EuiDataGridCellContent = memo(function (props) {
  var renderCellValue = props.renderCellValue,
      rest = _objectWithoutProperties(props, ["renderCellValue"]); // React is more permissible than the TS types indicate


  var CellElement = renderCellValue;
  return React.createElement(CellElement, _extends({
    isDetails: false,
    "data-test-subj": "cell-content"
  }, rest));
});
EuiDataGridCellContent.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  visibleRowIndex: PropTypes.number.isRequired,
  colIndex: PropTypes.number.isRequired,
  columnId: PropTypes.string.isRequired,
  columnType: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf([null])]),
  isExpandable: PropTypes.bool.isRequired,
  className: PropTypes.string,
  renderCellValue: PropTypes.oneOfType([PropTypes.func.isRequired, PropTypes.func.isRequired]).isRequired,
  setCellProps: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired
};
export class EuiDataGridCell extends Component {
  cellRef = createRef();
  popoverPanelRef = createRef();
  cellContentsRef = null;
  state = {
    cellProps: {},
    popoverIsOpen: false,
    isEntered: false,
    disableCellTabIndex: false
  };
  unsubscribeCell = () => {};
  static contextType = DataGridContext;
  getInteractables = () => {
    const tabbingRef = this.cellContentsRef;

    if (tabbingRef) {
      return tabbingRef.querySelectorAll('[data-datagrid-interactable=true]');
    }

    return [];
  };
  updateFocus = () => {
    const cell = this.cellRef.current;
    const {
      isFocused
    } = this.props;

    if (cell && isFocused) {
      // only update focus if we are not already focused on something in this cell
      let element = document.activeElement;

      while (element != null && element !== cell) {
        element = element.parentElement;
      }

      const doFocusUpdate = element !== cell;

      if (doFocusUpdate) {
        const interactables = this.getInteractables();

        if (this.props.isExpandable === false && interactables.length === 1) {
          // Only one element can be interacted with
          interactables[0].focus();
        } else {
          cell.focus();
        }
      }
    }
  };

  componentDidMount() {
    this.unsubscribeCell = this.context.onFocusUpdate([this.props.colIndex, this.props.visibleRowIndex], this.updateFocus);
  }

  componentWillUnmount() {
    if (this.unsubscribeCell) {
      this.unsubscribeCell();
    }
  }

  componentDidUpdate(prevProps) {
    const didFocusChange = prevProps.isFocused !== this.props.isFocused;

    if (didFocusChange) {
      this.updateFocus();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.rowIndex !== this.props.rowIndex) return true;
    if (nextProps.visibleRowIndex !== this.props.visibleRowIndex) return true;
    if (nextProps.colIndex !== this.props.colIndex) return true;
    if (nextProps.columnId !== this.props.columnId) return true;
    if (nextProps.columnType !== this.props.columnType) return true;
    if (nextProps.width !== this.props.width) return true;
    if (nextProps.renderCellValue !== this.props.renderCellValue) return true;
    if (nextProps.onCellFocus !== this.props.onCellFocus) return true;
    if (nextProps.isFocused !== this.props.isFocused) return true;
    if (nextProps.interactiveCellId !== this.props.interactiveCellId) return true;
    if (nextProps.popoverContent !== this.props.popoverContent) return true;
    if (nextState.cellProps !== this.state.cellProps) return true;
    if (nextState.popoverIsOpen !== this.state.popoverIsOpen) return true;
    if (nextState.isEntered !== this.state.isEntered) return true;
    if (nextState.disableCellTabIndex !== this.state.disableCellTabIndex) return true;
    return false;
  }

  setCellProps = cellProps => {
    this.setState({
      cellProps
    });
  };
  setCellContentsRef = ref => {
    this.cellContentsRef = ref;
    this.preventTabbing();
  };
  onFocus = () => {
    const {
      onCellFocus,
      colIndex,
      visibleRowIndex,
      isExpandable
    } = this.props;
    onCellFocus([colIndex, visibleRowIndex]);
    const interactables = this.getInteractables();

    if (interactables.length === 1 && isExpandable === false) {
      interactables[0].focus();
      this.setState({
        disableCellTabIndex: true
      });
    }
  };
  onBlur = () => {
    this.setState({
      disableCellTabIndex: false
    });
  };
  preventTabbing = () => {
    if (this.cellContentsRef) {
      const tabbables = tabbable(this.cellContentsRef);

      for (let i = 0; i < tabbables.length; i++) {
        const element = tabbables[i];
        element.setAttribute('tabIndex', '-1');
        element.setAttribute('data-datagrid-interactable', 'true');
      }
    }
  };
  enableTabbing = () => {
    if (this.cellContentsRef) {
      const interactables = this.getInteractables();

      for (let i = 0; i < interactables.length; i++) {
        const element = interactables[i];
        element.removeAttribute('tabIndex');
      }
    }
  };

  render() {
    const {
      width,
      isFocused,
      isExpandable,
      popoverContent: PopoverContent,
      interactiveCellId,
      columnType,
      onCellFocus,
      className,
      ...rest
    } = this.props;
    const {
      colIndex,
      rowIndex
    } = rest;
    const cellClasses = classNames('euiDataGridRowCell', {
      [`euiDataGridRowCell--${columnType}`]: columnType
    }, className);
    const cellProps = { ...this.state.cellProps,
      'data-test-subj': classNames('dataGridRowCell', this.state.cellProps['data-test-subj']),
      className: classNames(cellClasses, this.state.cellProps.className)
    };
    const widthStyle = width != null ? {
      width: `${width}px`
    } : {};

    if (cellProps.hasOwnProperty('style')) {
      cellProps.style = { ...cellProps.style,
        ...widthStyle
      };
    } else {
      cellProps.style = widthStyle;
    }

    const handleCellKeyDown = e => {
      if (isExpandable) {
        switch (e.key) {
          case 'Enter':
          case 'F2':
            e.preventDefault();
            this.setState({
              popoverIsOpen: true
            });
            break;
        }
      } else {
        if (e.key === 'Enter' || e.key === 'F2' || e.key === 'Escape') {
          const interactables = this.getInteractables();

          if (interactables.length >= 2) {
            switch (e.key) {
              case 'Enter':
                // `Enter` only activates the trap
                if (this.state.isEntered === false) {
                  this.enableTabbing();
                  this.setState({
                    isEntered: true
                  }); // result of this keypress is focus shifts to the first interactive element
                  // and then the browser fires the onClick event because that's how [Enter] works
                  // so we need to prevent that default action otherwise entering the trap triggers the first element

                  e.preventDefault();
                }

                break;

              case 'F2':
                // toggle interactives' focus trap
                this.setState(({
                  isEntered
                }) => {
                  if (isEntered) {
                    this.preventTabbing();
                  } else {
                    this.enableTabbing();
                  }

                  return {
                    isEntered: !isEntered
                  };
                });
                break;

              case 'Escape':
                // `Escape` only de-activates the trap
                this.preventTabbing();

                if (this.state.isEntered === true) {
                  this.setState({
                    isEntered: false
                  });
                }

                break;
            }
          }
        }
      }
    };

    const cellContentProps = { ...rest,
      setCellProps: this.setCellProps,
      columnType: columnType,
      isExpandable,
      isExpanded: this.state.popoverIsOpen,
      isDetails: false
    };
    const buttonIconClasses = classNames('euiDataGridRowCell__expandButtonIcon', {
      'euiDataGridRowCell__expandButtonIcon-isActive': this.state.popoverIsOpen
    });
    const buttonClasses = classNames('euiDataGridRowCell__expandButton', {
      'euiDataGridRowCell__expandButton-isActive': this.state.popoverIsOpen
    });
    const expandButton = <EuiI18n token="euiDataGridCell.expandButtonTitle" default="Click or hit enter to interact with cell content">
        {expandButtonTitle => <EuiButtonIcon className={buttonIconClasses} color="text" iconSize="s" iconType="expandMini" aria-hidden onClick={() => this.setState(({
        popoverIsOpen
      }) => ({
        popoverIsOpen: !popoverIsOpen
      }))} title={expandButtonTitle} />}
      </EuiI18n>;
    const screenReaderPosition = <EuiScreenReaderOnly>
        <p>
          <EuiI18n tokens={['euiDataGridCell.row', 'euiDataGridCell.column']} defaults={['Row', 'Column']}>
            {([row, column]) => <span>
                {row}: {rowIndex + 1}, {column}: {colIndex + 1}:
              </span>}
          </EuiI18n>
        </p>
      </EuiScreenReaderOnly>;
    let anchorContent = <EuiFocusTrap disabled={!this.state.isEntered} autoFocus={true} onDeactivation={() => {
      this.setState({
        isEntered: false
      }, this.preventTabbing);
    }} clickOutsideDisables={true}>
        <div className="euiDataGridRowCell__expandFlex">
          <EuiMutationObserver observerOptions={{
          subtree: true,
          childList: true
        }} onMutation={this.preventTabbing}>
            {mutationRef => {
            return <div ref={mutationRef} className="euiDataGridRowCell__expandContent">
                  {screenReaderPosition}
                  <div ref={this.setCellContentsRef} className="euiDataGridRowCell__truncate">
                    <EuiDataGridCellContent {...cellContentProps} />
                  </div>
                </div>;
          }}
          </EuiMutationObserver>
        </div>
      </EuiFocusTrap>;

    if (isExpandable) {
      anchorContent = <div className="euiDataGridRowCell__expandFlex">
          <EuiMutationObserver observerOptions={{
          subtree: true,
          childList: true
        }} onMutation={this.preventTabbing}>
            {mutationRef => {
            return <div ref={mutationRef} className="euiDataGridRowCell__expandContent">
                  {screenReaderPosition}
                  <div ref={this.setCellContentsRef} className="euiDataGridRowCell__truncate">
                    <EuiDataGridCellContent {...cellContentProps} />
                  </div>
                </div>;
          }}
          </EuiMutationObserver>
          <div className={buttonClasses}>{expandButton}</div>
        </div>;
    }

    let innerContent = anchorContent;

    if (isExpandable) {
      const CellElement = rest.renderCellValue;
      const popoverContent = <PopoverContent cellContentsElement={this.cellContentsRef}>
          <CellElement {...cellContentProps} isDetails={true} />
        </PopoverContent>;
      innerContent = <div className="euiDataGridRowCell__content">
          <EuiPopover anchorClassName="euiDataGridRowCell__expand" button={anchorContent} isOpen={this.state.popoverIsOpen} panelRef={ref => this.popoverPanelRef.current = ref} ownFocus panelClassName="euiDataGridRowCell__popover" zIndex={8001} display="block" closePopover={() => this.setState({
          popoverIsOpen: false
        })} onKeyDown={e => {
          if (e.key === 'F2' || e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.setState({
              popoverIsOpen: false
            });
          }
        }} onTrapDeactivation={this.updateFocus}>
            {popoverContent}
          </EuiPopover>
        </div>;
    }

    return <div role="gridcell" tabIndex={isFocused && !this.state.disableCellTabIndex ? 0 : -1} ref={this.cellRef} {...cellProps} data-test-subj="dataGridRowCell" onKeyDown={handleCellKeyDown} onFocus={this.onFocus} onBlur={this.onBlur}>
        {innerContent}
      </div>;
  }

}
EuiDataGridCell.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  visibleRowIndex: PropTypes.number.isRequired,
  colIndex: PropTypes.number.isRequired,
  columnId: PropTypes.string.isRequired,
  columnType: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf([null])]),
  width: PropTypes.number,
  isFocused: PropTypes.bool.isRequired,
  onCellFocus: PropTypes.func.isRequired,
  interactiveCellId: PropTypes.string.isRequired,
  isExpandable: PropTypes.bool.isRequired,
  className: PropTypes.string,
  popoverContent: PropTypes.elementType.isRequired,
  renderCellValue: PropTypes.oneOfType([PropTypes.func.isRequired, PropTypes.func.isRequired]).isRequired
};

try {
  // @ts-ignore
  EuiDataGridCell.displayName = "EuiDataGridCell"; // @ts-ignore

  EuiDataGridCell.__docgenInfo = {
    "description": "",
    "displayName": "EuiDataGridCell",
    "props": {
      "rowIndex": {
        "defaultValue": null,
        "description": "",
        "name": "rowIndex",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "visibleRowIndex": {
        "defaultValue": null,
        "description": "",
        "name": "visibleRowIndex",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "colIndex": {
        "defaultValue": null,
        "description": "",
        "name": "colIndex",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "columnId": {
        "defaultValue": null,
        "description": "",
        "name": "columnId",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "columnType": {
        "defaultValue": null,
        "description": "",
        "name": "columnType",
        "required": false,
        "type": {
          "name": "string | null | undefined"
        }
      },
      "width": {
        "defaultValue": null,
        "description": "",
        "name": "width",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "isFocused": {
        "defaultValue": null,
        "description": "",
        "name": "isFocused",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "onCellFocus": {
        "defaultValue": null,
        "description": "",
        "name": "onCellFocus",
        "required": true,
        "type": {
          "name": "Function"
        }
      },
      "interactiveCellId": {
        "defaultValue": null,
        "description": "",
        "name": "interactiveCellId",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "isExpandable": {
        "defaultValue": null,
        "description": "",
        "name": "isExpandable",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "popoverContent": {
        "defaultValue": null,
        "description": "",
        "name": "popoverContent",
        "required": true,
        "type": {
          "name": "ComponentType<EuiDataGridPopoverContentProps>"
        }
      },
      "renderCellValue": {
        "defaultValue": null,
        "description": "",
        "name": "renderCellValue",
        "required": true,
        "type": {
          "name": "((props: EuiDataGridCellValueElementProps) => ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<...>)> | null) | (new (props: EuiDataGridCellValueElementProps) => Component<...>) | ((props: EuiDataGrid..."
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/datagrid/data_grid_cell.tsx#EuiDataGridCell"] = {
      docgenInfo: EuiDataGridCell.__docgenInfo,
      name: "EuiDataGridCell",
      path: "src/components/datagrid/data_grid_cell.tsx#EuiDataGridCell"
    };
} catch (__react_docgen_typescript_loader_error) {}